import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Link2, Search, X, ChevronDown } from 'lucide-react';
import { AppInlineLoader, AppTableSkeleton } from '@/modules/shared/components/ui/AppLoading';
import { useEntityLookup, useEntityById } from '../../hooks/useEntityLookup';
import { useEntityAutoMapping } from '../../hooks/useEntityAutoMapping';
import { usePublicEntityLookup, usePublicEntityById } from '../../hooks/usePublicEntityLookup';
import { FormFieldDefinition } from '../../types/formBuilder';
import { EntityLookupResult } from '../../services/entityLookupService';
import { PublicEntityLookupResult } from '@/modules/shared/services/selfRegistrationService';
import { useDebounce } from '@/modules/shared/hooks/useDebounce';
import { useToast } from '@/modules/shared/components/ui/toast';

type FieldValue = string | number | boolean | string[] | Record<string, unknown>[] | null | undefined;

interface EntitySelectorFieldProps {
  field: FormFieldDefinition;
  value: FieldValue;
  onChange: (fieldName: string, value: FieldValue) => void;
  error?: string;
  disabled?: boolean;
  formValues?: Record<string, FieldValue>;
  /** Override programId (for contexts without route params) */
  programId?: string;
  /** Optional map from fieldId -> internalName for dependency resolution compatibility */
  dependencyFieldKeyMap?: Record<string, string>;
  /** When set, uses public (no-auth) API with program code instead of programId */
  publicProgramCode?: string;
}

export function EntitySelectorField({
  field,
  value,
  onChange,
  error,
  disabled,
  formValues,
  programId: programIdProp,
  dependencyFieldKeyMap,
  publicProgramCode,
}: EntitySelectorFieldProps) {
  const { showToast } = useToast();
  const { programId: routeProgramId } = useParams<{ programId: string }>();
  const programId = programIdProp || routeProgramId;
  const isPublicMode = !!publicProgramCode;
  const config = field.entitySelectorConfig;
  const [searchText, setSearchText] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const debouncedSearch = useDebounce(searchText, 300);
  const normalizedEntityType = config?.entityType?.toUpperCase();
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click / Escape so the panel never sits open once the user has
  // moved on — without this, the only way to dismiss it was another exact hit on a
  // result row or the trigger itself.
  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Resolve parent dependency if configured
  const parentEntityId = useMemo(() => {
    if (!config?.parentDependencyFieldId || !formValues) return undefined;
    const dependencyKey = config.parentDependencyFieldId;
    const mappedKey = dependencyFieldKeyMap?.[dependencyKey];
    const parentVal = formValues[dependencyKey] ?? (mappedKey ? formValues[mappedKey] : undefined);

    return parentVal ? String(parentVal) : undefined;
  }, [config?.parentDependencyFieldId, formValues, dependencyFieldKeyMap]);

  const requiresParentSelection = !!config?.parentDependencyFieldId;
  const waitingForParent = requiresParentSelection && !parentEntityId;
  const effectiveDisabled = !!disabled || waitingForParent;
  const shouldRunLookup = isOpen && !!config?.entityType && !isPublicMode && (!requiresParentSelection || !!parentEntityId);
  const shouldRunPublicLookup = isOpen && !!config?.entityType && isPublicMode && (!requiresParentSelection || !!parentEntityId);

  // ─── Authenticated search (standard mode) ─────────────────────────────
  const {
    data: authSearchResults,
    isLoading: isAuthSearching,
    error: authSearchError,
    isError: isAuthSearchError,
    isFetched: isAuthSearchFetched,
  } = useEntityLookup(
    programId,
    config?.entityType,
    {
      search: debouncedSearch || undefined,
      parentEntityId,
      status: normalizedEntityType === 'HOSPITAL' || normalizedEntityType === 'HCP' ? 'ACTIVE' : undefined,
      roleName: config?.roleName,
      pageSize: 20,
    },
    shouldRunLookup
  );

  // ─── Public search (self-registration mode) ───────────────────────────
  const {
    data: publicSearchResults,
    isLoading: isPublicSearching,
    error: publicSearchError,
    isError: isPublicSearchError,
    isFetched: isPublicSearchFetched,
  } = usePublicEntityLookup(
    publicProgramCode,
    config?.entityType,
    {
      search: debouncedSearch || undefined,
      parentEntityId,
      pageSize: 20,
    },
    shouldRunPublicLookup
  );

  const searchResults = isPublicMode ? publicSearchResults : authSearchResults;
  const isSearching = isPublicMode ? isPublicSearching : isAuthSearching;

  const lookupError = isPublicMode ? publicSearchError : authSearchError;
  const hasLookupError = isPublicMode ? isPublicSearchError : isAuthSearchError;
  const hasLookupFetched = isPublicMode ? isPublicSearchFetched : isAuthSearchFetched;
  const lastErrorToastRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isOpen || !hasLookupError || !lookupError) return;

    const asAny = lookupError as {
      response?: { data?: { message?: string; correlationId?: string } };
      message?: string;
    };
    const message = asAny.response?.data?.message || asAny.message || 'Failed to load options';
    const correlationId = asAny.response?.data?.correlationId;
    const toastKey = `${message}:${correlationId ?? ''}`;
    if (lastErrorToastRef.current === toastKey) return;
    lastErrorToastRef.current = toastKey;

    showToast(message, 'error', 5000, correlationId);
  }, [isOpen, hasLookupError, lookupError, showToast]);

  // ─── Resolve selected entity display ──────────────────────────────────
  const selectedId = value ? String(value) : undefined;

  const { data: authSelectedEntity } = useEntityById(
    programId,
    config?.entityType,
    !isPublicMode ? selectedId : undefined
  );

  const { data: publicSelectedEntity } = usePublicEntityById(
    publicProgramCode,
    config?.entityType,
    isPublicMode ? selectedId : undefined
  );

  const selectedEntity = isPublicMode ? publicSelectedEntity : authSelectedEntity;

  // A saved value can outlive its parent (parent field was later changed, or the
  // saved record's link is stale). Flag it instead of silently showing a value
  // that no longer belongs to the currently selected parent.
  const hasStaleParentMismatch =
    requiresParentSelection &&
    !!selectedEntity &&
    !!parentEntityId &&
    !!selectedEntity.parentEntityId &&
    selectedEntity.parentEntityId !== parentEntityId;

  // When the parent (e.g. Hospital) actually changes after mount, drop a selection made
  // under the old parent — it no longer applies. Distinct from hasStaleParentMismatch
  // above, which only flags a mismatch already present in loaded/saved data so we don't
  // silently wipe a historical record on open.
  const prevParentEntityIdRef = useRef(parentEntityId);
  useEffect(() => {
    const prevParentEntityId = prevParentEntityIdRef.current;
    prevParentEntityIdRef.current = parentEntityId;
    if (prevParentEntityId === undefined || prevParentEntityId === parentEntityId) return;
    if (selectedId) onChange(field.internalName, null);
  }, [parentEntityId, selectedId, onChange, field.internalName]);

  // Auto-mapping (only in authenticated mode — public mode maps on submission)
  useEntityAutoMapping({
    programId,
    entityType: config?.entityType,
    entityId: selectedId,
    config,
    onMappedFieldsResolved: useCallback(
      (mapped: Record<string, string>) => {
        Object.entries(mapped).forEach(([fieldName, fieldValue]) => {
          onChange(fieldName, fieldValue);
        });
      },
      [onChange]
    ),
  });

  const handleSelect = (entity: EntityLookupResult | PublicEntityLookupResult) => {
    onChange(field.internalName, entity.entityId);
    setIsOpen(false);
    setSearchText('');
  };

  const handleClear = () => {
    onChange(field.internalName, null);
  };

  const errorClass = error || hasStaleParentMismatch ? 'border-destructive' : '';

  return (
    <div className="relative w-full" ref={containerRef}>
      {/* Selected value display / trigger */}
      <div
        className={`flex items-center h-8 w-full rounded-md border bg-background px-2 text-xs ${errorClass} ${
          effectiveDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
        }`}
        onClick={() => !effectiveDisabled && setIsOpen(!isOpen)}
      >
        <Link2 className="w-3 h-3 text-muted-foreground mr-1.5 flex-shrink-0" />
        {selectedEntity ? (
          <span className="truncate flex-1">
            {selectedEntity.displayText}
            {selectedEntity.entityCode && (
              <span className="text-muted-foreground ml-1">({selectedEntity.entityCode})</span>
            )}
          </span>
        ) : (
          <span className="text-muted-foreground flex-1 truncate">
            {waitingForParent
              ? 'Select parent entity first'
              : field.placeholder || `Select ${config?.entityType || 'entity'}...`}
          </span>
        )}
        {selectedId && !effectiveDisabled && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            className="p-0.5 hover:bg-muted rounded"
          >
            <X className="w-3 h-3 text-muted-foreground" />
          </button>
        )}
        <ChevronDown className="w-3 h-3 text-muted-foreground ml-1" />
      </div>

      {/* Dropdown panel */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md">
          {/* Search input */}
          {config?.searchEnabled !== false && (
            <div className="flex items-center border-b px-2 py-1.5">
              <Search className="w-3 h-3 text-muted-foreground mr-1.5" />
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search..."
                className="flex-1 bg-transparent text-xs outline-none"
                autoFocus
              />
              {isSearching && <AppInlineLoader label="Loading content" />}
            </div>
          )}

          {/* Results list */}
          <div className="max-h-48 overflow-y-auto">
            {isSearching ? (
              <div className="px-2 py-2">
                <AppTableSkeleton rows={3} columns={1} />
              </div>
            ) : hasLookupError ? (
              <div className="px-2 py-3 text-xs text-center text-destructive">
                Failed to load options
              </div>
            ) : searchResults?.items && searchResults.items.length > 0 ? (
              searchResults.items.map((entity) => (
                <button
                  key={entity.entityId}
                  type="button"
                  className={`w-full flex items-center px-2 py-1.5 text-xs hover:bg-accent text-left ${
                    entity.entityId === selectedId ? 'bg-accent/50' : ''
                  }`}
                  onClick={() => handleSelect(entity)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{entity.displayText}</div>
                    {entity.entityCode && (
                      <div className="text-[11px] text-muted-foreground truncate">
                        {entity.entityCode}
                      </div>
                    )}
                  </div>
                  {entity.status && (
                    <span className="text-[11px] text-muted-foreground ml-2 flex-shrink-0">
                      {entity.status}
                    </span>
                  )}
                </button>
              ))
            ) : (
              <div className="px-2 py-3 text-xs text-center text-muted-foreground">
                {waitingForParent
                  ? 'Select parent entity to load options'
                  : hasLookupFetched
                  ? normalizedEntityType === 'HOSPITAL'
                    ? 'No hospitals found'
                    : normalizedEntityType === 'HCP'
                      ? 'No HCPs found'
                      : 'No results found'
                  : 'Start typing to search'}
              </div>
            )}
          </div>

          {/* Total count */}
          {searchResults && searchResults.totalCount > searchResults.items.length && (
            <div className="border-t px-2 py-1 text-[11px] text-muted-foreground text-center">
              Showing {searchResults.items.length} of {searchResults.totalCount}
            </div>
          )}
        </div>
      )}

      {hasStaleParentMismatch && (
        <p className="mt-1 text-[11px] text-destructive">
          Saved value no longer matches the selected parent. Choose an option to update it.
        </p>
      )}
    </div>
  );
}
